
class QuickAddCard {
  constructor(element) {
    this.element = element;
    this.productId = element.dataset.productId;

    try {
      this.variants = JSON.parse(element.dataset.variants || '[]');
    } catch (e) {
      console.error('Error parsing variants:', e);
      this.variants = [];
    }

    const activeBtn = this.element.querySelector('.quick-add-card__variant-option.active');
    this.activeVariantId = activeBtn?.dataset.variantId || (this.variants[0]?.id ?? null);
    this.cartState = {};
    this.isUpdating = false;
    this.init();
  }

  init() {
    this.setupControls();
    this.setupVariantSelector();
    this.loadCartState();
    this.refreshVariantUI();
    this.updateDisplay();
    this.updateVariantNote();
    this.updateAvailabilityUI();
  }

  setupControls() {
    this.element.querySelectorAll('.quick-add-card__btn--plus').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this.increaseQuantity();
      });
    });
    this.element.querySelectorAll('.quick-add-card__btn--minus').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this.decreaseQuantity();
      });
    });
  }

  setupVariantSelector() {
    this.element.querySelectorAll('.quick-add-card__variant-option').forEach(option => {
      option.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this.switchVariant(option.dataset.variantId);
      });
    });
  }

  getActiveVariant() {
    return this.variants.find(v => String(v.id) === String(this.activeVariantId));
  }

  updateAvailabilityUI() {
    const variant = this.getActiveVariant();
    if (!variant) return;
    const isAvailable = !!variant.available;
    const soldOutEl = this.element.querySelector('.quick-add-card__sold-out');
    this.element.classList.toggle('is-sold-out', !isAvailable);
    this.element.querySelectorAll('.quick-add-card__btn--plus').forEach(btn => {
      btn.disabled = !isAvailable;
      btn.setAttribute('aria-disabled', String(!isAvailable));
    });
    if (soldOutEl) soldOutEl.hidden = isAvailable;
  }

  refreshVariantUI() {
    const variant = this.getActiveVariant();
    if (!variant) return;

    const priceEl = this.element.querySelector('.price-item--regular');
    const compareEl = this.element.querySelector('.price-item--compare');
    const suffixEl = this.element.querySelector('.quick-add-card__price-suffix');
    const saleBadge = this.element.querySelector('.quick-add-card__sale-badge');

    if (priceEl && variant.price != null) {
      priceEl.textContent = this.formatPrice(variant.price);
    }

    const comparePrice = Number(variant.compare_at_price) || 0;
    const currentPrice = Number(variant.price) || 0;
    const isOnSale = comparePrice > currentPrice;

    if (compareEl) {
      compareEl.hidden = !isOnSale;
      compareEl.style.display = isOnSale ? '' : 'none';
      if (isOnSale) compareEl.textContent = this.formatPrice(comparePrice);
    }

    if (saleBadge) {
      saleBadge.hidden = !isOnSale;
      if (isOnSale) {
        const discount = Math.round(((comparePrice - currentPrice) / comparePrice) * 100);
        saleBadge.textContent = 'מבצע';
      }
    }

    if (suffixEl) {
      suffixEl.textContent = variant.unit_price_measurement
        ? this.getPriceSuffix(variant)
        : '';
    }

    this.element.querySelectorAll('.quick-add-card__variant-tag').forEach(tag => {
      tag.textContent = variant.option1 || '';
    });
    this.updateAvailabilityUI();
  }

  async switchVariant(variantId) {
    const oldVariantId = this.activeVariantId;
    const oldQuantity = this.cartState[oldVariantId] || 0;
    this.activeVariantId = variantId;

    this.element.querySelectorAll('.quick-add-card__variant-option').forEach(opt => {
      opt.classList.toggle('active', opt.dataset.variantId === variantId);
    });
    this.refreshVariantUI();

    if (oldQuantity > 0 && String(oldVariantId) !== String(variantId)) {
      delete this.cartState[oldVariantId];
      this.cartState[variantId] = oldQuantity;
      this.updateDisplay();
      await this.transferQuantityBetweenVariants(oldVariantId, variantId, oldQuantity);
    } else {
      this.updateDisplay();
    }
    this.updateVariantNote();
  }

  updateVariantNote() {
    const noteContainer = this.element.querySelector('.quick-add-card__variant-note');
    const notesWrapper = this.element.querySelector('.quick-add-card__variant-notes');
    if (!noteContainer || !notesWrapper) return;
    const noteEl = notesWrapper.querySelector(`[data-variant-id="${this.activeVariantId}"]`);
    noteContainer.textContent = noteEl?.dataset.note || '';
  }

  async transferQuantityBetweenVariants(fromVariantId, toVariantId, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [fromVariantId]: 0, [toVariantId]: quantity } })
      });
      if (response.ok) {
        const cart = await response.json();
        this.updateCartIcon(cart);
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } finally {
      this.isUpdating = false;
    }
  }

  getPriceSuffix(variant) {
    const unitMap = {
      kg: ' / ק״ג', g: ' / גרם', l: ' / ליטר',
      ml: ' / מ״ל', unit: ' / יחידה', item: ' / יחידה'
    };
    const unit = variant.unit_price_measurement?.reference_unit;
    return unitMap[unit] || (unit ? ` / ${unit}` : '');
  }

  formatPrice(price) {
    return `₪${(price / 100).toFixed(2)}`;
  }

  async loadCartState() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.cartState = {};
      cart.items.forEach(item => {
        this.cartState[item.variant_id] = item.quantity;
      });
      this.updateDisplay();
      this.updateAvailabilityUI();
    } catch (e) {
      console.error(e);
    }
  }

  getCurrentQuantity() {
    return this.cartState[this.activeVariantId] || 0;
  }

  async increaseQuantity() {
    const variant = this.getActiveVariant();
    if (!variant || !variant.available) return;
    await this.updateCart(this.getCurrentQuantity() + 1);
  }

  async decreaseQuantity() {
    const qty = this.getCurrentQuantity();
    if (qty > 0) await this.updateCart(qty - 1);
  }

  async updateCart(newQuantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    const oldQuantity = this.cartState[this.activeVariantId] || 0;
    this.cartState[this.activeVariantId] = newQuantity;
    if (newQuantity === 0) delete this.cartState[this.activeVariantId];
    this.updateDisplay();
    this.animateQuantityChange();
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [this.activeVariantId]: newQuantity } })
      });
      if (response.ok) {
        const cart = await response.json();
        this.updateCartIcon(cart);
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } finally {
      this.isUpdating = false;
      this.updateAvailabilityUI();
    }
  }

  updateCartIcon(cart) {
    document.querySelectorAll('.cart-count-bubble span').forEach(el => {
      el.textContent = cart.item_count;
    });
  }

  updateDisplay() {
    const qty = this.getCurrentQuantity();
    this.element.querySelectorAll('.quick-add-card__quantity').forEach(el => {
      el.textContent = qty;
    });
    this.element.classList.toggle('is-in-cart', qty > 0);
  }

  animateQuantityChange() {
    this.element.querySelectorAll('.quick-add-card__quantity').forEach(el => {
      el.classList.add('bump');
      setTimeout(() => el.classList.remove('bump'), 300);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.quick-add-card').forEach(card => {
    if (!card.quickAddInitialized) {
      card.quickAddInitialized = true;
      new QuickAddCard(card);
    }
  });
});

document.addEventListener('shopify:section:load', () => {
  document.querySelectorAll('.quick-add-card').forEach(card => {
    if (!card.quickAddInitialized) {
      card.quickAddInitialized = true;
      new QuickAddCard(card);
    }
  });
});
